import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Leaf, Zap, Sprout, CalendarClock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ChespiEnergyLogo } from "@/components/Logo";
import { useLang } from "@/i18n";

const pub = (f) => `${process.env.PUBLIC_URL}/${f}`;
const PHOTOS = ["Parc-fotovoltaic.jpg", "fotovoltaic.jpeg", "photovoltaic-g59c295d4d_1920.jpg"];

// Light-theme carousel for the Costinești solar park.
function Carousel() {
  const [idx, setIdx] = useState(0);
  const go = useCallback((n) => setIdx((n + PHOTOS.length) % PHOTOS.length), []);
  const next = useCallback(() => setIdx((i) => (i + 1) % PHOTOS.length), []);

  useEffect(() => {
    const id = setInterval(next, 6000);
    return () => clearInterval(id);
  }, [next]);

  return (
    <div className="relative overflow-hidden rounded-3xl border border-emerald-900/10 shadow-[0_40px_80px_-45px_rgba(6,78,59,0.45)]">
      <div
        className="flex transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${idx * 100}%)` }}
      >
        {PHOTOS.map((p) => (
          <img
            key={p}
            src={pub(p)}
            alt="Photovoltaic Park Costinești"
            className="aspect-[16/11] w-full flex-shrink-0 object-cover"
          />
        ))}
      </div>

      <button
        onClick={() => go(idx - 1)}
        aria-label="Previous"
        className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full border border-white/40 bg-[#04210f]/35 p-2 text-white backdrop-blur-md transition-all hover:bg-[#04210f]/60"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={next}
        aria-label="Next"
        className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full border border-white/40 bg-[#04210f]/35 p-2 text-white backdrop-blur-md transition-all hover:bg-[#04210f]/60"
      >
        <ChevronRight size={20} />
      </button>

      <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 gap-2.5">
        {PHOTOS.map((p, i) => (
          <button
            key={p}
            onClick={() => go(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === idx ? "w-7 bg-white" : "w-2 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

const STAT_ICONS = [Sprout, Zap, CalendarClock];

export default function EnergyPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const e = t.energy;

  useEffect(() => {
    window.scrollTo({ top: 0 });
    // The global body background is near-black; force it green here so no dark bar
    // shows below the closing band (or on over-scroll). Restored on unmount.
    document.body.style.backgroundColor = "#064e3b";
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  const backHome = () => {
    navigate("/");
    setTimeout(() => document.getElementById("sectors")?.scrollIntoView({ behavior: "smooth" }), 90);
  };

  return (
    <main className="bg-white text-ink">
      {/* ───────────── HERO — solar field, deep-green wash, white type ───────────── */}
      <section className="relative flex min-h-[88vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={pub("photovoltaic-g59c295d4d_1920.jpg")}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#022c22]/95 via-[#065f46]/85 to-[#064e3b]/95" />
          <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#022c22]/80 to-transparent" />
        </div>
        <div className="pointer-events-none absolute -right-20 top-10 h-[55vh] w-[55vh] rounded-full bg-lime-300/20 blur-3xl" />

        <div className="container-luxe relative w-full pb-24 pt-32 md:pt-40">
          <Reveal>
            <ChespiEnergyLogo
              markSize={48}
              lionClass="h-9 w-9 sm:h-11 sm:w-11 lg:h-12 lg:w-12"
              textClass="text-base sm:text-2xl lg:text-3xl"
              frameClass="border-[3px] px-2.5 py-1 sm:px-4 sm:py-1.5"
            />
            <p className="mt-6 max-w-2xl font-serif text-xl italic text-emerald-100/90 sm:text-2xl">
              {e.aboutLead}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-7 max-w-2xl text-sm leading-relaxed text-white/75 md:text-base">{e.p2}</p>
          </Reveal>
        </div>
      </section>

      {/* ───────────── PROJECT — carousel + details on white ───────────── */}
      <section className="bg-white py-20 md:py-28">
        <div className="container-luxe">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <Carousel />
            </Reveal>

            <Reveal delay={0.1}>
              <span className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.32em] text-emerald-700">
                <span className="h-px w-8 bg-emerald-600/50" /> {e.overline}
              </span>
              <h2 className="mt-5 font-serif text-3xl font-light leading-snug text-[#064e3b] sm:text-4xl md:text-5xl">
                {e.projectTitle}
              </h2>
              <p className="mt-6 text-base leading-relaxed text-ink/60 md:text-lg">{e.projectText}</p>
            </Reveal>
          </div>

          {/* spec cards */}
          <Reveal delay={0.15}>
            <div className="mt-14 grid grid-cols-3 gap-3 sm:gap-5">
              {e.stats.map((s, i) => {
                const Icon = STAT_ICONS[i % STAT_ICONS.length];
                return (
                  <div
                    key={s.label}
                    className="rounded-2xl border border-emerald-900/20 bg-[#064e3b] px-3 py-7 text-center shadow-[0_24px_50px_-30px_rgba(6,78,59,0.6)] transition-shadow duration-300 hover:shadow-[0_30px_60px_-30px_rgba(6,78,59,0.75)] sm:px-5"
                  >
                    <Icon className="mx-auto mb-3 text-emerald-300" size={24} />
                    <div className="font-serif text-2xl text-white sm:text-3xl">{s.value}</div>
                    <div className="mt-1.5 text-[9px] uppercase tracking-[0.16em] text-emerald-200/70 sm:text-[10px]">
                      {s.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────── COMMITMENT — deep green band (matches the spec cards) ───────────── */}
      <section className="relative bg-[#064e3b] py-24 md:py-32">
        <div className="container-luxe relative">
          <Reveal className="mx-auto max-w-3xl text-center">
            <span className="mx-auto mb-7 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-emerald-300">
              <Leaf size={20} />
            </span>
            <p className="font-serif text-xl font-light leading-relaxed text-white sm:text-2xl md:text-3xl">
              {e.p1}
            </p>
            <button
              onClick={backHome}
              className="mt-12 inline-flex items-center gap-2 rounded-full border border-emerald-300/40 px-7 py-3 text-xs uppercase tracking-[0.2em] text-emerald-100 transition-all duration-300 hover:bg-emerald-300 hover:text-[#064e3b]"
            >
              <ArrowLeft size={16} /> {e.back}
            </button>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
